import styled, { keyframes } from 'styled-components';

// Fade-in animation for the HeroTitle
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Pulse animation for the submit button on hover
const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(102, 32, 45, 0.4);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(102, 32, 45, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(102, 32, 45, 0);
  }
`;

// Slide-down animation for the mobile menu
const slideDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

// Ripple effect animation for link taps
const ripple = keyframes`
  to {
    transform: scale(4);
    opacity: 0;
  }
`;

// Logo spin animation
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Ensure box-sizing is applied globally
const globalStyles = `
  *, *:before, *:after {
    box-sizing: border-box;
  }

  :root {
    --primary-gradient: linear-gradient(90deg, #66202D 0%, #F89622 100%);
    --text-color: #F5F5F5;
    --active-glow: #FFE4CD;
    --neumorphic-shadow: 8px 8px 16px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.1);
  }
`;

export const Container = styled.div`
  ${globalStyles}
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f9f9f9; /* Fallback for debugging */
`;

export const HeroSection = styled.div`
  background-image: url(${props => props.bgImage});
  background-size: cover;
  background-position: center;
  min-height: 35vh;
  position: relative;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.2);
    z-index: 1;
  }

  @media (max-width: 768px) {
    min-height: 30vh;
  }
`;

export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 3rem;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

export const Logo = styled.img`
  width: 118px;
  height: 67px;
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 57px;
  }
`;

export const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-family: 'Lato', sans-serif;
  margin-left: auto;

  a {
    position: relative;
    padding-bottom: 4px;
    color: white;
    text-decoration: none;
    font-weight: 700;
    font-size: 16px;
    text-transform: uppercase;
    transition: color 0.3s ease, transform 0.3s ease;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0%;
      height: 2px;
      background-color: #F89622;
      transition: width 0.3s ease;
    }

    &:hover {
      color: #FFE4CD;
      transform: translateY(-2px);
    }

    &:hover::after {
      width: 100%;
    }

    &.active::after {
      width: 100%;
    }
  }

  a.register {
    padding: 0.5rem 1.5rem;
    border: 1px solid #F89622;
    border-radius: 246px;
    background-color: transparent;
    color: white;
    transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;

    &::after {
      display: none;
    }

    &:hover {
      background-color: white;
      color: #F89622;
      transform: translateY(-2px);
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const Hamburger = styled.div`
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 20px;
  cursor: pointer;
  z-index: 1001;

  @media (max-width: 768px) {
    display: flex;
  }

  span {
    width: 100%;
    height: 3px;
    background-color: white;
    transition: all 0.3s ease;
  }

  &.open span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }

  &.open span:nth-child(2) {
    opacity: 0;
  }

  &.open span:nth-child(3) {
    transform: rotate(-45deg) translate(7px, -7px);
  }
`;

export const MobileMenuOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  z-index: 999;

  @media (max-width: 768px) {
    display: ${props => (props.isOpen ? 'block' : 'none')};
  }
`;

export const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 70vh;
  background: var(--primary-gradient);
  backdrop-filter: blur(15px);
  flex-direction: column;
  align-items: center;
  padding: 2rem 1.5rem 1rem;
  border-bottom-left-radius: 32px;
  border-bottom-right-radius: 32px;
  box-shadow: var(--neumorphic-shadow);
  z-index: 1000;
  animation: ${slideDown} 0.4s ease-out forwards;
  clip-path: polygon(0 10%, 50% 0, 100% 10%, 100% 100%, 0 100%);

  @media (max-width: 768px) {
    display: ${props => (props.isOpen ? 'flex' : 'none')};
  }
`;

export const GrabberHandle = styled.div`
  width: 40px;
  height: 5px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  margin-top: 1rem;
  cursor: pointer;
`;

export const MobileMenuLogo = styled.img`
  width: 90px;
  height: 50px;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
  animation: ${spin} 1s ease-in-out;
`;

export const Divider = styled.hr`
  width: 80%;
  border: none;
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin: 1rem 0;
`;

export const MenuLink = styled.a`
  position: relative;
  color: var(--text-color);
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 20px;
  text-transform: uppercase;
  margin: 1rem 0;
  padding: 0.75rem 1.5rem;
  width: 100%;
  text-align: center;
  transition: color 0.3s ease, transform 0.3s ease;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  box-shadow: var(--neumorphic-shadow);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  display: block;

  &.active {
    color: var(--active-glow);
    box-shadow: 0 0 10px var(--active-glow);
  }

  &:hover {
    color: var(--active-glow);
    transform: scale(1.02);
  }

  &:focus {
    outline: 2px solid var(--active-glow);
    outline-offset: 2px;
  }

  // Ripple effect on tap
  .ripple {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 228, 205, 0.4);
    transform: scale(0);
    animation: ${ripple} 0.6s linear;
    pointer-events: none;
  }
`;

export const HeroTitle = styled.h2`
  font-size: 48px;
  font-family: 'PP Neue Montreal', sans-serif;
  font-weight: 500;
  line-height: 200%;
  text-align: center;
  color: white;
  margin-top: auto;
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 1s ease-out forwards;

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 1.5rem;
  }
`;

export const ContentContainer = styled.div`
  padding: 3rem;
  flex-grow: 1;
  font-family: 'PP Neue Montreal', sans-serif;
  color: #1E1E1E;
  background: #f9f9f9;
  box-sizing: border-box;
  position: relative;
  z-index: 1; /* Ensure content is below mobile menu */

  & > div {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
    max-width: 1200px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const Section = styled.div`
  background: linear-gradient(145deg, #ffffff, #f5f5f5);
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-sizing: border-box;
  flex: 1;
  min-width: 300px;
  max-width: 100%;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const SectionTitle = styled.h5`
  font-size: 28px;
  font-family: 'PP Neue Montreal', sans-serif;
  font-weight: 500;
  margin: 0 0 1.5rem;
  color: #1E1E1E;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 50%;
    height: 3px;
    background: linear-gradient(90deg, #66202D 0%, #F89622 100%);
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  @media (max-width: 768px) {
    font-size: 24px;
    margin: 0 0 1rem;
  }
`;

export const Paragraph = styled.p`
  font-size: 18px;
  font-family: 'PP Neue Montreal', sans-serif;
  line-height: 170%;
  margin-bottom: 0.5rem;
  color: #1E1E1E;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: ${fadeIn} 0.8s ease-out forwards;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

export const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
`;

export const InputField = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  font-size: 16px;
  font-family: 'PP Neue Montreal', sans-serif;
  background: #FAFAFA;
  border: 1px solid #AEB4AC;
  border-radius: 138px;
  color: #4A4A4A;
  transition: border-color 0.3s ease, background 0.3s ease, transform 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: transparent;
    border-image: linear-gradient(90deg, #66202D 0%, #F89622 100%) 1;
    background: #FFFFFF;
    transform: scale(1.01);
  }

  &:not(:placeholder-shown) + label,
  &:focus + label {
    transform: translateY(-2.5rem) translateX(-0.5rem) scale(0.85);
    color: #66202D;
    background: #FFFFFF;
    padding: 0 0.5rem;
  }

  &::placeholder {
    color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 0.75rem 1rem;
  }
`;

export const InputLabel = styled.label`
  position: absolute;
  top: 1rem;
  left: 1.5rem;
  font-size: 16px;
  font-family: 'PP Neue Montreal', sans-serif;
  color: #4A4A4A;
  pointer-events: none;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: 14px;
    top: 0.75rem;
    left: 1rem;
  }
`;

export const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem 1.5rem;
  font-size: 16px;
  font-family: 'PP Neue Montreal', sans-serif;
  background: #FAFAFA;
  border: 1px solid #AEB4AC;
  border-radius: 16px;
  color: #4A4A4A;
  resize: vertical;
  transition: border-color 0.3s ease, background 0.3s ease, transform 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: transparent;
    border-image: linear-gradient(90deg, #66202D 0%, #F89622 100%) 1;
    background: #FFFFFF;
    transform: scale(1.01);
  }

  &:not(:placeholder-shown) + label,
  &:focus + label {
    transform: translateY(-2.5rem) translateX(-0.5rem) scale(0.85);
    color: #66202D;
    background: #FFFFFF;
    padding: 0 0.5rem;
  }

  &::placeholder {
    color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 0.75rem 1rem;
  }
`;

export const TextAreaLabel = styled.label`
  position: absolute;
  top: 1rem;
  left: 1.5rem;
  font-size: 16px;
  font-family: 'PP Neue Montreal', sans-serif;
  color: #4A4A4A;
  pointer-events: none;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: 14px;
    top: 0.75rem;
    left: 1rem;
  }
`;

export const CheckboxLabel = styled.label`
  font-size: 16px;
  font-family: 'PP Neue Montreal', sans-serif;
  color: #1E1E1E;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  input[type="checkbox"] {
    appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid #AEB4AC;
    border-radius: 4px;
    transition: all 0.3s ease;
    position: relative;

    &:checked {
      border-color: #66202D;
      background: #F89622;
    }

    &:checked::after {
      content: '✔';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 12px;
    }
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export const SubmitButton = styled.button`
  width: 186px;
  height: 39px;
  padding: 0.5rem;
  border: none;
  border-radius: 390px;
  background: linear-gradient(90deg, #66202D 0%, #F89622 100%);
  color: white;
  font-size: 14px;
  font-family: 'PP Neue Montreal', sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    animation: ${pulse} 1.5s infinite;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 150px;
    height: 35px;
    font-size: 12px;
  }
`;

export const AlertMessage = styled.div`
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-family: 'PP Neue Montreal', sans-serif;
  font-size: 16px;
  color: ${props => (props.severity === 'success' ? '#2e7d32' : '#d32f2f')};
  background-color: ${props => (props.severity === 'success' ? '#e8f5e9' : '#ffebee')};
  border: 1px solid ${props => (props.severity === 'success' ? '#a5d6a7' : '#ef5350')};
  animation: ${fadeIn} 0.5s ease-out forwards;
  box-shadow: 0 0 10px ${props => (props.severity === 'success' ? 'rgba(46, 125, 50, 0.2)' : 'rgba(211, 47, 47, 0.2)')};

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 0.5rem;
  }
`;

export const Footer = styled.footer`
  padding: 1rem 3rem;
  font-size: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  color: #1E1E1E;
  font-family: 'PP Neue Montreal', sans-serif;
  position: relative;
  z-index: 1; /* Ensure footer is below mobile menu */

  div a {
    margin-left: 1rem;
    color: #1E1E1E;
    text-decoration: none;
    transition: color 0.3s ease, transform 0.3s ease;

    &:hover {
      color: #66202D;
      text-decoration: underline;
      transform: translateY(-2px);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 1.5rem;
    text-align: center;

    div a {
      margin: 0 0.5rem;
    }
  }
`;